import { useState, useRef, useCallback, useEffect } from "react";
import { getSession } from "../../api/api";
import { normalizeVoiceAppKey, type VoiceAppKey } from "../../utils/voiceApp";
import { AVATAROpenAIEndpoint } from "../../../env";

export const SAMPLE_RATE = 24_000;
const DEFAULT_REALTIME_MODEL = "gpt-realtime-2";

function getRealtimeCallUrl(model: string | null | undefined): string {
  const endpoint = new URL(AVATAROpenAIEndpoint);
  if (endpoint.protocol !== "https:") {
    throw new Error("Azure OpenAI realtime endpoint must use HTTPS.");
  }

  endpoint.pathname = "/openai/v1/realtime/calls";
  endpoint.search = "";
  endpoint.searchParams.set("model", model || DEFAULT_REALTIME_MODEL);
  return endpoint.toString();
}

function createDataChannelSocket(channel: RTCDataChannel, peer: RTCPeerConnection): WebSocket {
  const socket = Object.create(null) as WebSocket;
  const readyState = () => {
    switch (channel.readyState) {
      case "connecting": return WebSocket.CONNECTING;
      case "open": return WebSocket.OPEN;
      case "closing": return WebSocket.CLOSING;
      default: return WebSocket.CLOSED;
    }
  };

  Object.defineProperties(socket, {
    readyState: { get: readyState },
    send: { value: (data: string) => channel.send(data) },
    close: { value: () => { channel.close(); peer.close(); } },
    onopen: { set: (handler: ((this: WebSocket, event: Event) => unknown) | null) => {
      if (handler) channel.addEventListener("open", handler as EventListener);
    } },
    onmessage: { set: (handler: ((this: WebSocket, event: MessageEvent) => unknown) | null) => {
      if (handler) channel.addEventListener("message", handler as EventListener);
    } },
    onerror: { set: (handler: ((this: WebSocket, event: Event) => unknown) | null) => {
      if (handler) channel.addEventListener("error", handler as EventListener);
    } },
    onclose: { set: (handler: ((this: WebSocket, event: CloseEvent) => unknown) | null) => {
      if (handler) channel.addEventListener("close", handler as EventListener);
    } },
  });
  return socket;
}

const WORKLET_CODE = `
class PCMProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0]?.[0];
    if (ch) this.port.postMessage(new Float32Array(ch));
    return true;
  }
}
registerProcessor('pcm-processor', PCMProcessor);
`;

function createRealtimeResponseEvent(): string {
  return JSON.stringify({
    type: "response.create",
    response: { output_modalities: ["audio"] },
  });
}

export type VoiceStatus =
  | "idle"
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "interrupted"
  | "error";

function float32ToBase64PCM(samples: Float32Array): string {
  const pcm16 = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  const bytes = new Uint8Array(pcm16.buffer);
  let bin = "";
  const CHUNK = 8192;
  for (let i = 0; i < bytes.length; i += CHUNK)
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  return btoa(bin);
}

function base64PCMToFloat32(b64: string): Float32Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm16 = new Int16Array(bytes.buffer);
  const f32 = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) f32[i] = pcm16[i] / 32768;
  return f32;
}

function appendNonOverlappingText(existing: string, incoming: string): string {
  if (!incoming) return "";
  if (!existing) return incoming;
  if (existing.includes(incoming)) return "";
  if (existing.endsWith(incoming)) return "";
  if (incoming.startsWith(existing)) return incoming.slice(existing.length);

  const maxOverlap = Math.min(existing.length, incoming.length);
  for (let size = maxOverlap; size > 0; size--) {
    if (existing.slice(-size) === incoming.slice(0, size)) {
      return incoming.slice(size);
    }
  }
  return incoming;
}

export function useRealtimeVoice(
  onTranscript: (text: string) => void,
  onToolCall?: (name: string, args: Record<string, unknown>, callId: string) => Promise<unknown>,
  onAITranscript?: (delta: string, done: boolean, replace?: boolean) => void,
  onToolResult?: (result: unknown, name: string) => void,
  selectedVoiceApp?: string | null,
) {
  const selectedVoiceAppKey = normalizeVoiceAppKey(selectedVoiceApp);
  const activeVoiceAppRef = useRef<string | null>(null);
  const activeVoiceAppKeyRef = useRef<VoiceAppKey | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const rtcPeerRef = useRef<RTCPeerConnection | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const micCtxRef = useRef<AudioContext | null>(null);
  const playCtxRef = useRef<AudioContext | null>(null);
  const workletRef = useRef<AudioWorkletNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const workletBlobRef = useRef<string | null>(null);
  const scheduledEndRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const aiSpeakingRef = useRef(false);
  const droppingRef = useRef(false);

  const responseItemIdRef = useRef<string | null>(null);
  const audioSamplesScheduledRef = useRef<number>(0);
  const responseAudioStartAtRef = useRef<number | null>(null);

  const unmuteMicTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTranscriptRef = useRef<{ text: string; at: number }>({ text: "", at: 0 });
  const dropUntilRef = useRef<number>(0);
  const initialResponseSentRef = useRef<boolean>(false);
  const ignoreTranscriptUntilRef = useRef<number>(0);
  const suppressMicRef = useRef<boolean>(false);
  const aiTranscriptDoneRef = useRef<boolean>(false);
  const aiTranscriptBufferRef = useRef<string>("");
  const displayedTranscriptRef = useRef<string>("");
  const transcriptQueueRef = useRef<string[]>([]);
  const transcriptTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingTranscriptDoneRef = useRef<boolean>(false);
  const pendingFinalTranscriptRef = useRef<string>("");
  const pendingTranscriptReplaceRef = useRef<boolean>(false);
  const skipNextTranscriptFinalizeRef = useRef<boolean>(false);

  const [micActive, setMicActive] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>("idle");
  const effectiveVoiceAppKey =
    (micActive ? activeVoiceAppKeyRef.current : null) ?? selectedVoiceAppKey;
  const postResponseIgnoreMs =
    effectiveVoiceAppKey === "lipione" || effectiveVoiceAppKey === "jarvisone"
      ? 2500
      : 600;

  const clearTranscriptTimer = useCallback(() => {
    if (transcriptTimerRef.current) {
      clearTimeout(transcriptTimerRef.current);
      transcriptTimerRef.current = null;
    }
  }, []);

  const finalizeQueuedTranscript = useCallback(() => {
    if (!onAITranscript) return;

    const finalTranscript =
      pendingFinalTranscriptRef.current || displayedTranscriptRef.current;
    if (finalTranscript && pendingTranscriptReplaceRef.current) {
      displayedTranscriptRef.current = finalTranscript;
      onAITranscript(finalTranscript, false, true);
    }

    pendingTranscriptDoneRef.current = false;
    pendingFinalTranscriptRef.current = "";
    pendingTranscriptReplaceRef.current = false;
    onAITranscript("", true);
  }, [onAITranscript]);

  const flushTranscriptQueue = useCallback(() => {
    if (!onAITranscript) return;
    if (transcriptTimerRef.current || !transcriptQueueRef.current.length) return;

    const pump = () => {
      const chunk = transcriptQueueRef.current.shift();
      if (chunk) {
        displayedTranscriptRef.current += chunk;
        onAITranscript(chunk, false);
      }

      if (transcriptQueueRef.current.length) {
        const nextChunk = transcriptQueueRef.current[0] || "";
        const trimmed = nextChunk.trim();
        const delay =
          !trimmed ? 80
            : /^[,.;:!?]+$/.test(trimmed) ? 140
              : trimmed.length <= 3 ? 170
                : 220;
        transcriptTimerRef.current = setTimeout(pump, delay);
      } else {
        transcriptTimerRef.current = null;
        if (pendingTranscriptDoneRef.current) {
          finalizeQueuedTranscript();
        }
      }
    };

    transcriptTimerRef.current = setTimeout(pump, 220);
  }, [finalizeQueuedTranscript, onAITranscript]);

  const queueAITranscript = useCallback((chunk: string) => {
    if (!chunk || !onAITranscript) return;

    const parts = chunk.match(/\S+\s*|\s+/g) || [chunk];
    transcriptQueueRef.current.push(...parts);
    flushTranscriptQueue();
  }, [flushTranscriptQueue, onAITranscript]);

  const setStatus = useCallback((s: VoiceStatus, ms = 0, force = false) => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (ms > 0 && !force) { timerRef.current = setTimeout(() => setVoiceStatus(s), ms); }
    else { setVoiceStatus(s); }
  }, []);

  const getPlayCtx = useCallback(() => {
    if (!playCtxRef.current || playCtxRef.current.state === "closed") {
      playCtxRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
      scheduledEndRef.current = 0;
    }
    if (playCtxRef.current.state === "suspended") playCtxRef.current.resume();
    return playCtxRef.current;
  }, []);

  const killPlayback = useCallback(() => {
    try { playCtxRef.current?.close(); } catch { }
    playCtxRef.current = null;
    scheduledEndRef.current = 0;
  }, []);

  const playAudio = useCallback((b64: string) => {
    if (droppingRef.current) return;
    try {
      const ctx = getPlayCtx();
      const f32 = base64PCMToFloat32(b64);
      const buf = ctx.createBuffer(1, f32.length, SAMPLE_RATE);
      buf.getChannelData(0).set(f32);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      // Keep a tiny playout buffer on first chunk to reduce jitter/choppiness.
      const leadIn = responseAudioStartAtRef.current === null ? 0.08 : 0;
      const at = Math.max(ctx.currentTime + leadIn, scheduledEndRef.current);
      if (responseAudioStartAtRef.current === null) {
        responseAudioStartAtRef.current = at;
      }
      src.start(at);
      scheduledEndRef.current = at + buf.duration;
      audioSamplesScheduledRef.current += f32.length;
    } catch { }
  }, [getPlayCtx]);

  const connectWS = useCallback(async (): Promise<WebSocket> => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return wsRef.current;

    setStatus("connecting");
    const voiceAppForSession = activeVoiceAppRef.current ?? selectedVoiceApp;
    const voiceAppKeyForSession =
      activeVoiceAppKeyRef.current ?? normalizeVoiceAppKey(voiceAppForSession);
    const session = await getSession(voiceAppForSession);
    const token = session?.client_secret?.value;
    if (!token) {
      throw new Error("Realtime session did not include a client secret.");
    }
    const callUrl = getRealtimeCallUrl(session?.model);
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: SAMPLE_RATE,
        sampleSize: 16,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    streamRef.current = stream;

    const peer = new RTCPeerConnection();
    rtcPeerRef.current = peer;
    // Keep ICE details in the console so failed networks can be diagnosed from
    // the browser, rather than treating an SDP 201 as proof media connected.
    peer.addEventListener("iceconnectionstatechange", () => {
      console.info("[Realtime] ICE connection state:", peer.iceConnectionState);
    });
    peer.addEventListener("connectionstatechange", () => {
      console.info("[Realtime] peer connection state:", peer.connectionState);
    });
    peer.addEventListener("icecandidateerror", (event) => {
      console.warn("[Realtime] ICE candidate error:", {
        url: event.url,
        errorCode: event.errorCode,
        errorText: event.errorText,
      });
    });
    stream.getTracks().forEach((track) => peer.addTrack(track, stream));
    peer.ontrack = (event) => {
      const audio = new Audio();
      audio.autoplay = true;
      audio.srcObject = event.streams[0] || new MediaStream([event.track]);
      remoteAudioRef.current = audio;
      void audio.play().catch((error) => {
        console.warn("[Realtime] Remote audio playback requires user interaction:", error);
      });
    };
    const channel = peer.createDataChannel("oai-events");
    const ws = createDataChannelSocket(channel, peer);

    return new Promise((resolve, reject) => {

      const connTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          const iceState = peer.iceConnectionState;
          ws.close();
          reject(new Error(`Realtime data channel timed out (ICE: ${iceState}). Check whether this network allows WebRTC UDP traffic.`));
        }
      }, 10_000);

      let sessionConfigured = false;
      let connectionOpened = false;

      function interruptAI() {
        droppingRef.current = true;
        dropUntilRef.current = Date.now() + 1200;
        ignoreTranscriptUntilRef.current = 0;
        suppressMicRef.current = false;

        const scheduledMs = Math.floor(
          (audioSamplesScheduledRef.current / SAMPLE_RATE) * 1000,
        );
        const heardMs =
          playCtxRef.current && responseAudioStartAtRef.current !== null
            ? Math.max(
              0,
              Math.floor(
                (playCtxRef.current.currentTime - responseAudioStartAtRef.current) * 1000,
              ),
            )
            : 0;
        const audioEndMs = Math.min(scheduledMs, heardMs);

        if (responseItemIdRef.current) {
          ws.send(JSON.stringify({
            type: "conversation.item.truncate",
            item_id: responseItemIdRef.current,
            content_index: 0,
            audio_end_ms: audioEndMs,
          }));
        }

        killPlayback();
        ws.send(JSON.stringify({ type: "response.cancel" }));
        ws.send(JSON.stringify({ type: "input_audio_buffer.clear" }));

        aiSpeakingRef.current = false;
        responseItemIdRef.current = null;
        audioSamplesScheduledRef.current = 0;
        responseAudioStartAtRef.current = null;

        if (unmuteMicTimerRef.current) {
          clearTimeout(unmuteMicTimerRef.current);
          unmuteMicTimerRef.current = null;
        }

        clearTranscriptTimer();
        transcriptQueueRef.current = [];
        displayedTranscriptRef.current = "";
        onAITranscript?.("", true);

        setStatus("interrupted");
        setStatus("listening", 150);
        setTimeout(() => {
          if (Date.now() >= dropUntilRef.current) droppingRef.current = false;
        }, 1200);
      }

      function stopAI() {
        droppingRef.current = true;
        dropUntilRef.current = Date.now() + 1200;
        suppressMicRef.current = false;

        if (responseItemIdRef.current) {
          const scheduledMs = Math.floor(
            (audioSamplesScheduledRef.current / SAMPLE_RATE) * 1000,
          );
          const heardMs =
            playCtxRef.current && responseAudioStartAtRef.current !== null
              ? Math.max(
                0,
                Math.floor(
                  (playCtxRef.current.currentTime - responseAudioStartAtRef.current) * 1000,
                ),
              )
              : 0;
          const audioEndMs = Math.min(scheduledMs, heardMs);
          ws.send(JSON.stringify({
            type: "conversation.item.truncate",
            item_id: responseItemIdRef.current,
            content_index: 0,
            audio_end_ms: audioEndMs,
          }));
        }

        killPlayback();
        ws.send(JSON.stringify({ type: "response.cancel" }));
        ws.send(JSON.stringify({ type: "input_audio_buffer.clear" }));

        aiSpeakingRef.current = false;
        responseItemIdRef.current = null;
        audioSamplesScheduledRef.current = 0;
        responseAudioStartAtRef.current = null;
        if (Date.now() >= dropUntilRef.current) droppingRef.current = false;

        if (unmuteMicTimerRef.current) {
          clearTimeout(unmuteMicTimerRef.current);
          unmuteMicTimerRef.current = null;
        }

        clearTranscriptTimer();
        transcriptQueueRef.current = [];
        displayedTranscriptRef.current = "";
        onAITranscript?.("", true);
        setStatus("listening", 0, true);
      }

      ws.onopen = () => {
        clearTimeout(connTimeout);
        connectionOpened = true;
        wsRef.current = ws;
        initialResponseSentRef.current = false;
        if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
          ws.send(JSON.stringify({
            type: "session.update",
            session: {
              type: "realtime",
              audio: {
                input: {
                  transcription: { model: "whisper-1", language: "en" },
                  turn_detection: {
                    type: "server_vad",
                    threshold: 0.6,
                    prefix_padding_ms: 500,
                    silence_duration_ms: 1100,
                    create_response: false,
                  },
                },
              },
            },
          }));
        }
        resolve(ws);
      };

      ws.onerror = () => {
        clearTimeout(connTimeout);
        setStatus("error");
        reject(new Error("Azure Realtime data channel failed to open."));
      };
      ws.onclose = () => {
        wsRef.current = null;
        if (!connectionOpened) reject(new Error("Azure Realtime data channel closed before opening."));
        setStatus("idle");
      };

      ws.onmessage = async (evt) => {
        let msg: Record<string, any>;
        try { msg = JSON.parse(evt.data); } catch { return; }

        switch (msg.type) {
          case "session.created":
            sessionConfigured = true;
            if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
              break;
            }
            if (!initialResponseSentRef.current) {
              setStatus("thinking", 0, true);
              ws.send(createRealtimeResponseEvent());
              initialResponseSentRef.current = true;
            }
            break;

          case "session.updated":
            if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
              setStatus("listening", 0, true);
              break;
            }
            // Non-Lipi apps already send their initial response on
            // `session.created`. If we also trigger from `session.updated`,
            // Support Pop / Policy Buddy can generate the same greeting twice.
            if (sessionConfigured) {
              break;
            }
            break;

          case "response.output_item.added":
          case "response.output_item.created":
            if (msg.item?.type === "message" && msg.item?.role === "assistant") {
              aiTranscriptDoneRef.current = false;
              aiTranscriptBufferRef.current = "";
              clearTranscriptTimer();
              transcriptQueueRef.current = [];
              displayedTranscriptRef.current = "";
              pendingTranscriptDoneRef.current = false;
              pendingFinalTranscriptRef.current = "";
              pendingTranscriptReplaceRef.current = false;
              responseItemIdRef.current = msg.item.id as string;
              audioSamplesScheduledRef.current = 0;
              responseAudioStartAtRef.current = null;
            }
            break;

          case "input_audio_buffer.speech_started":
            if (aiSpeakingRef.current) {
              interruptAI();
              break;
            }
            if (Date.now() < ignoreTranscriptUntilRef.current) {
              break;
            } else {
              setStatus("listening", 0, true);
            }
            break;

          case "input_audio_buffer.speech_stopped":
            if (Date.now() < ignoreTranscriptUntilRef.current) {
              break;
            }
            if (!aiSpeakingRef.current) setStatus("thinking");
            break;

          case "conversation.item.input_audio_transcription.completed": {
            const t = ((msg.transcript as string) || "").replace(/\s+/g, " ").trim();
            if (!t) break;
            if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
              console.log("[Lipi One transcript]", t);
            }
            if (Date.now() < ignoreTranscriptUntilRef.current) {
              setStatus("listening", 0, true);
              break;
            }
            const now = Date.now();
            if (t === lastTranscriptRef.current.text && now - lastTranscriptRef.current.at < 1200) {
              break;
            }
            lastTranscriptRef.current = { text: t, at: now };
            onTranscript(t);
            setStatus("thinking", 0, true);
            ws.send(createRealtimeResponseEvent());
            break;
          }

          case "response.audio.delta":
          case "response.output_audio.delta":
            if (droppingRef.current || Date.now() < dropUntilRef.current || !msg.delta) break;
            if (!aiSpeakingRef.current) {
              aiSpeakingRef.current = true;
              // Keep streaming mic audio during playback so the user can
              // barge in and interrupt the current answer mid-response.
              suppressMicRef.current = false;
              setStatus("speaking", 0, true);
            }
            playAudio(msg.delta);
            break;

          case "response.audio_transcript.delta":
          case "response.output_audio_transcript.delta":
            if (!droppingRef.current && msg.delta) {
              const composedTranscript =
                displayedTranscriptRef.current + transcriptQueueRef.current.join("");
              const normalizedDelta = appendNonOverlappingText(
                composedTranscript || aiTranscriptBufferRef.current,
                String(msg.delta)
              );
              if (!normalizedDelta) {
                break;
              }
              aiTranscriptBufferRef.current += normalizedDelta;
              if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
                console.log("[Lipi One model transcript delta]", normalizedDelta);
              }
              queueAITranscript(normalizedDelta);
            }
            break;

          case "response.audio_transcript.done":
          case "response.output_audio_transcript.done":
            aiTranscriptDoneRef.current = true;
            if (voiceAppKeyForSession === "lipione" || voiceAppKeyForSession === "jarvisone") {
              console.log("[Lipi One model transcript done]", msg.transcript || "");
            }
            {
              const finalTranscript = String(msg.transcript || aiTranscriptBufferRef.current || "");
              pendingFinalTranscriptRef.current = finalTranscript;
              const composedTranscript =
                displayedTranscriptRef.current + transcriptQueueRef.current.join("");

              if (finalTranscript.startsWith(composedTranscript)) {
                const remaining = finalTranscript.slice(composedTranscript.length);
                if (remaining) {
                  const parts = remaining.match(/\S+\s*|\s+/g) || [remaining];
                  transcriptQueueRef.current.push(...parts);
                  flushTranscriptQueue();
                }
                pendingTranscriptReplaceRef.current = false;
              } else if (!composedTranscript.startsWith(finalTranscript)) {
                // Defer correction until the stream finishes so we don't dump the
                // entire response onto the screen mid-speech.
                pendingTranscriptReplaceRef.current = true;
              } else {
                pendingTranscriptReplaceRef.current = false;
              }
            }
            if (!transcriptQueueRef.current.length && !transcriptTimerRef.current) {
              finalizeQueuedTranscript();
            } else {
              pendingTranscriptDoneRef.current = true;
            }
            break;

          case "response.done": {
            droppingRef.current = false;

            if (
              skipNextTranscriptFinalizeRef.current &&
              voiceAppKeyForSession !== "lipione" &&
              voiceAppKeyForSession !== "jarvisone"
            ) {
              skipNextTranscriptFinalizeRef.current = false;
            } else if (!aiTranscriptDoneRef.current) {
              onAITranscript?.("", true);
            }

            const ctx = playCtxRef.current;
            const msUntilSilent = ctx
              ? Math.max(0, (scheduledEndRef.current - ctx.currentTime) * 1000)
              : 0;

            if (unmuteMicTimerRef.current) clearTimeout(unmuteMicTimerRef.current);
            unmuteMicTimerRef.current = setTimeout(() => {
              aiSpeakingRef.current = false;
              suppressMicRef.current = false;
              responseItemIdRef.current = null;
              audioSamplesScheduledRef.current = 0;
              responseAudioStartAtRef.current = null;
              aiTranscriptBufferRef.current = "";
              ignoreTranscriptUntilRef.current = Date.now() + postResponseIgnoreMs;
              if (!pendingTranscriptDoneRef.current) {
                clearTranscriptTimer();
                transcriptQueueRef.current = [];
                displayedTranscriptRef.current = "";
                pendingFinalTranscriptRef.current = "";
                pendingTranscriptDoneRef.current = false;
                pendingTranscriptReplaceRef.current = false;
              }
              ws.send(JSON.stringify({ type: "input_audio_buffer.clear" }));
              setStatus("listening");
              unmuteMicTimerRef.current = null;
            }, msUntilSilent + 200);

            break;
          }

          case "response.function_call_arguments.done": {
            if (!onToolCall) break;
            setStatus("thinking");
            try {
              if (voiceAppKeyForSession !== "lipione" && voiceAppKeyForSession !== "jarvisone") {
                skipNextTranscriptFinalizeRef.current = true;
                clearTranscriptTimer();
                transcriptQueueRef.current = [];
                displayedTranscriptRef.current = "";
                aiTranscriptBufferRef.current = "";
                pendingFinalTranscriptRef.current = "";
                pendingTranscriptDoneRef.current = false;
                pendingTranscriptReplaceRef.current = false;
                onAITranscript?.("", false, true);
              }
              const result = await onToolCall(msg.name, JSON.parse(msg.arguments), msg.call_id);
              onToolResult?.(result, msg.name);
              ws.send(JSON.stringify({
                type: "conversation.item.create",
                item: { type: "function_call_output", call_id: msg.call_id, output: JSON.stringify(result) },
              }));
              ws.send(createRealtimeResponseEvent());
            } catch (e) {
              console.error("[Realtime] tool error:", e);
              setStatus("listening", 0, true);
            }
            break;
          }

          case "error":
            console.error("[Realtime] error:", msg.error);
            setStatus("listening", 300);
            break;

          default: break;
        }
      };
      void (async () => {
        const offer = await peer.createOffer();
        await peer.setLocalDescription(offer);
        const sdp = peer.localDescription?.sdp || offer.sdp;
        if (!sdp) throw new Error("Could not create the Azure Realtime SDP offer.");

        const response = await fetch(callUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/sdp",
          },
          body: sdp,
        });
        if (!response.ok) {
          const details = await response.text();
          throw new Error(`Azure Realtime WebRTC negotiation failed (${response.status}): ${details}`);
        }

        const answer = await response.text();
        await peer.setRemoteDescription({ type: "answer", sdp: answer });
      })().catch((error: unknown) => {
        clearTimeout(connTimeout);
        setStatus("error");
        stream.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        peer.close();
        rtcPeerRef.current = null;
        reject(error instanceof Error ? error : new Error(String(error)));
      });
    });
  }, [onTranscript, onAITranscript, playAudio, killPlayback, postResponseIgnoreMs, setStatus, onToolCall, onToolResult, selectedVoiceApp]);

  const startMic = useCallback(async (_ws: WebSocket) => {
    setMicActive(true);
  }, []);

  const startSession = useCallback(async () => {
    try {
      activeVoiceAppRef.current = selectedVoiceApp ?? null;
      activeVoiceAppKeyRef.current = selectedVoiceAppKey;
      const ws = await connectWS();
      await startMic(ws);
    } catch (err) {
      activeVoiceAppRef.current = null;
      activeVoiceAppKeyRef.current = null;
      console.error("[Realtime] start failed:", err);
      setStatus("error");
    }
  }, [connectWS, selectedVoiceApp, selectedVoiceAppKey, startMic, setStatus]);

  const stopSession = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (unmuteMicTimerRef.current) { clearTimeout(unmuteMicTimerRef.current); unmuteMicTimerRef.current = null; }

    workletRef.current?.port.close();
    workletRef.current?.disconnect();
    workletRef.current = null;

    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;

    micCtxRef.current?.close();
    micCtxRef.current = null;

    killPlayback();
    clearTranscriptTimer();
    wsRef.current?.close();
    rtcPeerRef.current?.close();
    rtcPeerRef.current = null;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.pause();
      remoteAudioRef.current.srcObject = null;
      remoteAudioRef.current = null;
    }
    wsRef.current = null;

    aiSpeakingRef.current = false;
    droppingRef.current = false;
    activeVoiceAppRef.current = null;
    activeVoiceAppKeyRef.current = null;
    responseItemIdRef.current = null;
    audioSamplesScheduledRef.current = 0;
    responseAudioStartAtRef.current = null;
    lastTranscriptRef.current = { text: "", at: 0 };
    initialResponseSentRef.current = false;
    ignoreTranscriptUntilRef.current = 0;
    suppressMicRef.current = false;
    aiTranscriptBufferRef.current = "";
    transcriptQueueRef.current = [];
    displayedTranscriptRef.current = "";

    setMicActive(false);
    setVoiceStatus("idle");
  }, [clearTranscriptTimer, killPlayback]);

  const toggleSession = useCallback(async () => {
    if (micActive) stopSession();
    else await startSession();
  }, [micActive, startSession, stopSession]);

  const sendTextMessage = useCallback((text: string) => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    wsRef.current.send(JSON.stringify({
      type: "conversation.item.create",
      item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
    }));
    wsRef.current.send(createRealtimeResponseEvent());
  }, []);

  useEffect(() => {
    if (!micActive) return;
    if (activeVoiceAppKeyRef.current) return;
    if (!normalizeVoiceAppKey(selectedVoiceApp)) {
      stopSession();
    }
  }, [micActive, selectedVoiceApp, stopSession]);

  useEffect(() => {
    return () => {
      stopSession();
      if (workletBlobRef.current) {
        URL.revokeObjectURL(workletBlobRef.current);
        workletBlobRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { micActive, voiceStatus, toggleSession, sendTextMessage };
}


